// eslint-disable-next-line no-restricted-syntax
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import minMax from 'dayjs/plugin/minMax';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import { locale as defaultLocale } from '@/utils/_shared/i18n';
import { AvailableLocale } from '@/i18n/LanguageProvider/shared';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isToday);

export default dayjs;

export const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Depending on the browser language detected, this is used to help figure out which dayjs locale
// to import and use it dynamically in order to have the dates formatted according to the locale
//
// ** We'll need to manually add in the necessary locale files as we go **
//
// Optimized dayjs locale loading with explicit imports that Vite can analyze
const LOCALE_IMPORTS = {
  en: () => import('dayjs/locale/en'),
  fr: () => import('dayjs/locale/fr'),
  es: () => import('dayjs/locale/es'),
  it: () => import('dayjs/locale/it'),
  pl: () => import('dayjs/locale/pl'),
  pt: () => import('dayjs/locale/pt'),
  ht: () => import('dayjs/locale/ht'),
  de: () => import('dayjs/locale/de'),
} as const;

type SupportedLocale = keyof typeof LOCALE_IMPORTS;

export const loadDayjsLocale = async (locale?: AvailableLocale) => {
  // Assuming locale is in a format like en-US, en-GB, etc. We'd just want the first part 'en'.
  const localeToUse = (locale || defaultLocale).split(
    '-',
  )[0] as SupportedLocale;

  if (localeToUse === 'en') {
    // Return early if locale is equal to 'en' since dayjs uses that by default
    return;
  }

  // Check if we have an explicit import for this locale
  const localeImport = LOCALE_IMPORTS[localeToUse];
  if (!localeImport) {
    console.warn(
      `Locale '${localeToUse}' is not supported. Supported locales are: ${Object.keys(LOCALE_IMPORTS).join(', ')}`,
    );
    return;
  }

  try {
    // Dynamically import the locale using explicit import function
    await localeImport();

    // This is what formats the date correctly according to the locale
    dayjs.locale(localeToUse);
  } catch (error) {
    console.error(
      `Failed to dynamically load the locale '${localeToUse}'. Error:`,
      error,
    );
  }
};
